import {useCallback, useEffect, useState} from 'react';
import {fetchIcrSimilarity, recomputeIcrSimilarity} from '../../../firebase/firestore/icr';

const POLL_MS = 4000;

/**
 * Load ICR similarity payload; poll while job is queued/running.
 */
export function useIcrSimilarity(icrId, enabled = true) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [recomputing, setRecomputing] = useState(false);

    const reload = useCallback(async () => {
        if (!icrId || !enabled) {
            setData(null);
            return null;
        }
        try {
            const next = await fetchIcrSimilarity(icrId);
            setData(next);
            setError(null);
            return next;
        } catch (e) {
            setError(e?.message || 'Failed to load similarity');
            return null;
        }
    }, [icrId, enabled]);

    useEffect(() => {
        if (!icrId || !enabled) {
            setData(null);
            setLoading(false);
            setError(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        reload().finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [icrId, enabled, reload]);

    const running = Boolean(data?.job?.running);

    useEffect(() => {
        if (!icrId || !enabled || !running) return undefined;
        const timer = setInterval(() => {
            reload();
        }, POLL_MS);
        return () => clearInterval(timer);
    }, [icrId, enabled, running, reload]);

    const recompute = useCallback(async () => {
        if (!icrId) return;
        setRecomputing(true);
        try {
            await recomputeIcrSimilarity(icrId);
            await reload();
        } catch (e) {
            setError(e?.message || 'Failed to recompute similarity');
        } finally {
            setRecomputing(false);
        }
    }, [icrId, reload]);

    return {data, loading, error, running, recomputing, reload, recompute};
}
